import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TrendingUp, Landmark, Wallet, RefreshCw, AlertTriangle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_BAR = {
  Training: 'bg-emerald-500', Venue: 'bg-amber-500', Consultancy: 'bg-sky-500', Unclassified: 'bg-slate-400',
};
const PERIODS = [
  { key: 'fy_total', label: 'FY Total' },
  { key: 'Q1', label: 'Q1 (Apr-Jun)' },
  { key: 'Q2', label: 'Q2 (Jul-Sep)' },
  { key: 'Q3', label: 'Q3 (Oct-Dec)' },
  { key: 'Q4', label: 'Q4 (Jan-Mar)' },
];

const QuarterlyStatement = ({ globalFY, getAuthHeaders, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusScope, setStatusScope] = useState('commitment');
  const [period, setPeriod] = useState('fy_total');
  const fmt = (v) => formatCurrency(v);
  const pct = (r) => `${(r * 100).toFixed(0)}%`;

  const fetchData = useCallback(async () => {
    if (!globalFY) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pnl/statement-quarterly?financial_year=${globalFY}&status_scope=${statusScope}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load quarterly statement');
    } finally {
      setLoading(false);
    }
  }, [globalFY, statusScope, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="quarterly-loading"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>;
  }
  if (!data) return null;

  const cur = period === 'fy_total' ? data.fy_total : data.quarters[period];
  const ms = cur.money_story;
  const cp = cur.corporate_pnl;

  return (
    <div className="space-y-6" data-testid="quarterly-statement">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Quarterly P&amp;L</h2>
          <p className="text-sm text-slate-500">Bucketed by the linked CRM project's PO&nbsp;Date · FY {globalFY}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusScope} onValueChange={setStatusScope}>
            <SelectTrigger className="w-[260px]" data-testid="quarterly-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="commitment">All expenses except Rejected (commitment)</SelectItem>
              <SelectItem value="realized">Only Paid + Partially Paid (realized)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="quarterly-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Period pills */}
      <div className="flex flex-wrap gap-2" data-testid="quarterly-period-pills">
        {PERIODS.map((p) => {
          const pms = p.key === 'fy_total' ? data.fy_total.money_story : data.quarters[p.key].money_story;
          const active = period === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`period-pill-${p.key}`}
              className={`px-4 py-2 rounded-xl border text-left transition-colors ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}
            >
              <div className="text-xs font-medium opacity-80">{p.label}</div>
              <div className="text-sm font-bold tabular-nums">{fmt(pms.corporate_net_pnl)}</div>
            </button>
          );
        })}
      </div>

      {/* Money story */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="quarterly-money-story">
        <HeroCard tone="slate" icon={<TrendingUp className="w-4 h-4" />} label="Valid Revenue" value={fmt(ms.valid_revenue)} sub={`${ms.project_count} estimates (excl GST)`} testid="q-valid-revenue" />
        <HeroCard tone="indigo" icon={<Landmark className="w-4 h-4" />} label="Funder Earnings" value={fmt(ms.funder_earnings)} sub="Funder share" testid="q-funder-earnings" />
        <HeroCard tone="emerald" icon={<Wallet className="w-4 h-4" />} label="Corporate Team Net P&L" value={fmt(ms.corporate_net_pnl)} sub="After funder share & expenses" testid="q-corp-net" />
      </div>
      {(ms.unclassified_count > 0 || ms.discrepancy_count > 0) && (
        <div className="flex flex-wrap gap-2">
          {ms.unclassified_count > 0 && <Badge variant="outline" className="border-slate-300 text-slate-600 gap-1"><HelpCircle className="w-3 h-3" />{ms.unclassified_count} unclassified</Badge>}
          {ms.discrepancy_count > 0 && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 gap-1"><AlertTriangle className="w-3 h-3" />{ms.discrepancy_count} type discrepancy</Badge>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business split */}
        <Card className="border-slate-200 overflow-hidden">
          <Table data-testid="quarterly-split-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Type</TableHead><TableHead className="text-center">%</TableHead><TableHead className="text-center">#</TableHead>
                <TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Funder</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cur.business_split.map((s) => (
                <TableRow key={s.type} className={s.count === 0 ? 'opacity-50' : ''}>
                  <TableCell><span className="inline-flex items-center gap-2 font-medium"><span className={`w-2.5 h-2.5 rounded-full ${TYPE_BAR[s.type]}`} />{s.type}</span></TableCell>
                  <TableCell className="text-center">{pct(s.funder_rate)}</TableCell>
                  <TableCell className="text-center tabular-nums">{s.count}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(s.revenue_excl_gst)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(s.funder_share)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Corporate P&L waterfall */}
        <Card className="border-emerald-200">
          <CardContent className="pt-6">
            <div className="space-y-1 text-sm" data-testid="quarterly-corp-pnl">
              <Row label="Valid Revenue" value={fmt(cp.valid_revenue)} strong />
              <Row label="Less: Funder Share" value={`(${fmt(cp.funder_share)})`} negative />
              <Row label="Revenue after Funder Share" value={fmt(cp.revenue_after_funder)} strong divider />
              <Row label="Less: Project Expenses" value={`(${fmt(cp.project_expenses)})`} negative />
              <Row label="Less: HR Salary (advanced by Funder)" value={`(${fmt(cp.hr_salary)})`} negative />
              <Row label="Less: HR Non-Salary" value={`(${fmt(cp.hr_other)})`} negative />
              <div className="flex items-center justify-between rounded-lg bg-emerald-600 text-white px-4 py-3 mt-3">
                <span className="font-semibold">Corporate Team Net P&amp;L</span>
                <span className="text-xl font-bold tabular-nums">{fmt(cp.net_pnl)}</span>
              </div>
              {cur.funder_settlement && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="quarterly-funder-settlement">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Funder Share</span><span className="tabular-nums">{fmt(cur.funder_settlement.funder_share)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Salary Advance (loan)</span><span className="tabular-nums">{fmt(cur.funder_settlement.salary_advance)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Less: Repaid</span><span className="tabular-nums text-emerald-700">({fmt(cur.funder_settlement.repaid)})</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-amber-200 pt-1.5 mt-1.5">
                    <span className="font-semibold text-amber-900 text-sm">Outstanding to Funder</span>
                    <span className="font-bold text-amber-700 tabular-nums">{fmt(cur.funder_settlement.outstanding)}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const HeroCard = ({ tone, icon, label, value, sub, testid }) => {
  const tones = {
    slate: 'from-slate-900 to-slate-800 text-white',
    indigo: 'from-indigo-600 to-violet-600 text-white',
    emerald: 'from-emerald-600 to-teal-600 text-white',
  };
  return (
    <div className={`rounded-2xl p-6 bg-gradient-to-br ${tones[tone]} shadow-lg`}>
      <div className="flex items-center gap-2 text-sm font-medium opacity-90">{icon}{label}</div>
      <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums" data-testid={testid}>{value}</p>
      <p className="mt-1 text-xs opacity-75">{sub}</p>
    </div>
  );
};

const Row = ({ label, value, strong, negative, divider }) => (
  <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg ${divider ? 'bg-slate-100' : ''}`}>
    <span className={strong ? 'font-semibold text-slate-900' : 'text-slate-600'}>{label}</span>
    <span className={`tabular-nums ${negative ? 'text-red-600' : strong ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{value}</span>
  </div>
);

export default QuarterlyStatement;
